<template>
	<div class="validators__container validators__container--span">
		<label :for="`${column.name}-validators`">Validators</label>
		<MultiSelect
			:id="`${column.name}-validators`"
			v-model="validatorsWithoutRequired"
			filter
			option-label="columnValidator"
			placeholder="Select validators"
			scroll-height="300px"
			:options="availableValidators">
			<template #value="slotProps">
				<div
					v-if="slotProps.value?.length > 0"
					class="validators__select-value">
					<div class="validators__name">
						{{
							slotProps.value
								.map((v: ColumnValidator) => snakeCaseToLabel(v.columnValidator))
								.join(', ')
						}}
					</div>
				</div>
				<div
					v-else
					class="validators__select-value">
					<div class="validators__placeholder">Select validators</div>
				</div>
			</template>
			<template #option="slotProps">
				<div style="display: flex; flex-direction: column">
					<div style="font-size: 14px; color: var(--p-text-color)">
						{{ snakeCaseToLabel(slotProps.option.columnValidator) }}
					</div>
				</div>
			</template>
		</MultiSelect>
	</div>
</template>

<script setup lang="ts">
import MultiSelect from 'primevue/multiselect'
import { computed, watch } from 'vue'
import {
	type ColumnValidator,
	getSupportedValidators,
	typeConstraints,
} from '@codecannon/schemas/interfaces/typeConstraints'
import { snakeCaseToLabel } from '@codecannon/schemas/utils/strings'
import { supportedFakerMethods } from '@codecannon/schemas/interfaces/fakerMethods'
import type { EnrichedColumn } from '@codecannon/schemas/schemas/EnrichedColumn'

const props = defineProps<{
	column: EnrichedColumn
}>()

const availableValidators = computed(() => {
	return getSupportedValidators(props.column.type)
})

const validators = computed({ get: getValidators, set: setValidators })
const validatorsWithoutRequired = computed({
	get: () => validators.value.filter((v) => v.columnValidator !== 'required'),
	set: setValidators,
})

watch(
	() => props.column.type,
	() => {
		// If there is no faker method available for the selected type the column can't be required
		if (Object.values(supportedFakerMethods[props.column.type]?.supported ?? {}).length === 0) {
			setRequired(false)
		}
		if (!props.column.typeConstraints) return
		props.column.typeConstraints = props.column.typeConstraints.filter(
			(tc) =>
				Object.values(availableValidators.value).find((val) => val.columnValidator === tc.name) ||
				tc.type !== 'validator' ||
				(tc.type === 'validator' && tc.name === 'required'),
		)
	},
	{ deep: true },
)

function setRequired(required: boolean) {
	if (!props.column.typeConstraints) props.column.typeConstraints = []

	const requiredIndex = props.column.typeConstraints.find((v) => v.name === 'required')

	if (required) {
		if (requiredIndex) return
		setValidators([
			...validatorsWithoutRequired.value,
			typeConstraints.required.columnValidators[0],
		])
	} else {
		const validatorIndex = props.column.typeConstraints.findIndex((v) => v.name === 'required')
		if (validatorIndex === -1) return
		props.column.typeConstraints = props.column.typeConstraints?.toSpliced(validatorIndex, 1)
	}
}

function getValidators(): ColumnValidator[] {
	return (props.column.typeConstraints?.filter((v) => v.type === 'validator') ?? [])
		.map((v) => typeConstraints[v.name as keyof typeof typeConstraints].columnValidators[0])
		.filter((v) => !!v)
}

function setValidators(newValidators: ColumnValidator[]) {
	if (!props.column.typeConstraints) props.column.typeConstraints = []

	props.column.typeConstraints = [
		...(newValidators.map((v) => ({
			type: 'validator',
			name: v.columnValidator,
			params: [],
		})) satisfies EnrichedColumn['typeConstraints']),
		...props.column.typeConstraints.filter(
			(tc) => tc.type !== 'validator' || (tc.type === 'validator' && tc.name === 'required'),
		),
	]
}
</script>

<style lang="scss" scoped>
.validators__container {
	display: flex;
	flex-direction: column;
	gap: 5px;
	margin-bottom: 16px;

	&:last-of-type {
		margin-bottom: 0;
	}

	&--span {
		grid-column: 1 / -1;
	}
}

.validators__select-value {
	display: flex;
	flex-direction: column;

	.validators__name {
		font-size: 14px;
		color: var(--p-text-color);
	}

	.validators__placeholder {
		font-size: 14px;
		color: var(--p-text-muted-color);
	}
}
</style>
